// ABOUTME: The one jobs view (the Work panel and the header's Work button): what needs the owner
// first (cards waiting for their OK, and jobs stuck past their time to finish, each with Retry /
// Dismiss), then what is running now (each agent's run with its latest step, and system jobs
// grouped by kind with their age), what failed (each with Dismiss, remembered on this device;
// a failure leaves the list after a day anyway) and what finished recently. Rows open where the
// work is.
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Clock, Loader2, ShieldAlert } from 'lucide-react'
import { AGENT_RUN_ACTIVITY_KEY, listRuns, type AgentRun } from '@/api/agentRuns'
import { fetchAgentRoles } from '@/api/agentRoles'
import { jobsApi, type JobAction, type JobItem } from '@/api/jobs'
import { Button } from '@/components/ui/button'
import { usePendingApprovalsCount } from '@/hooks/usePendingApprovalsCount'
import { useJobsStore } from '@/stores/useJobsStore'
import { useUiStore } from '@/stores/useUiStore'
import { describeJobFailure } from '@/lib/jobFailureCopy'
import { CHAT_THREAD_PATH } from '@/hooks/useIsChatHome'

const POLL_MS = 5_000
const DISMISSED_KEY = 'maxflow.dismissed-failed-jobs'
const MAX_DISMISSED = 100
const RECENT_GROUPS = 5
const ACTIVE = new Set(['queued', 'running', 'awaiting_input'])
const ACTION_LABEL: Record<JobAction, string> = { retry: 'Retry', dismiss: 'Dismiss' }

/** "40 s", "3 min", "5 h", "2 days" since `iso`. */
function age(iso: string | null): string {
  if (!iso) return ''
  const s = Math.max(1, Math.round((Date.now() - Date.parse(iso)) / 1000))
  if (s < 60) return `${s} s`
  if (s < 3600) return `${Math.floor(s / 60)} min`
  if (s < 86400) return `${Math.floor(s / 3600)} h`
  const days = Math.floor(s / 86400)
  return `${days} day${days === 1 ? '' : 's'}`
}

function latestStep(run: AgentRun): string {
  const step = [...run.steps].reverse().find((s) => s.kind !== 'thinking')
  return step ? (step.subject ? `${step.title}: ${step.subject}` : step.title) : 'Starting'
}

function readDismissed(): string[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

/** Failed jobs the owner dismissed on this device, and a way to dismiss one. */
function useDismissedFailures(): [Set<string>, (key: string) => void] {
  const [keys, setKeys] = useState(readDismissed)
  const dismiss = (key: string) => {
    const next = [key, ...keys.filter((k) => k !== key)].slice(0, MAX_DISMISSED)
    setKeys(next)
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(next))
    } catch {
      // Storage unavailable: dismissed for this visit only.
    }
  }
  return [new Set(keys), dismiss]
}

/** A job's kind as people read it: the title before any ':' detail ("Upload", "Import"). */
function kind(job: JobItem): string {
  return job.title.split(':')[0].trim() || job.type
}

function byKind(jobs: JobItem[]): [string, JobItem[]][] {
  const groups = new Map<string, JobItem[]>()
  for (const job of jobs) groups.set(kind(job), [...(groups.get(kind(job)) ?? []), job])
  return [...groups.entries()]
}

function Row({
  icon,
  name,
  detail,
  to,
  testId,
  children,
}: {
  icon: React.ReactNode
  name: string
  detail: string
  to?: string | null
  testId?: string
  children?: React.ReactNode
}) {
  const body = (
    <>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{name}</span>
        <span className="text-muted-foreground block truncate text-xs">{detail}</span>
      </span>
    </>
  )
  const style = 'flex min-h-11 min-w-0 flex-1 items-start gap-2 rounded-md px-2 py-1.5'
  return (
    <li className="flex items-center gap-1" data-testid={testId ?? 'jobs-row'}>
      {to ? (
        <Link to={to} className={`${style} hover:bg-muted`}>
          {body}
        </Link>
      ) : (
        <div className={style}>{body}</div>
      )}
      {children}
    </li>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1">
      <h3 className="text-muted-foreground px-2 text-xs font-medium tracking-wide uppercase">
        {title}
      </h3>
      <ul>{children}</ul>
    </section>
  )
}

function StuckRow({ job }: { job: JobItem }) {
  const loadJobs = useJobsStore((s) => s.loadJobs)
  const showToast = useUiStore((s) => s.showToast)
  const [busy, setBusy] = useState(false)
  const settle = async (action: JobAction) => {
    setBusy(true)
    try {
      await jobsApi.settleJob(job, action)
      showToast({ type: 'success', title: action === 'retry' ? 'Trying it again' : 'Stopped' })
      await loadJobs()
    } catch (err) {
      showToast({ type: 'error', title: (err as Error).message })
    } finally {
      setBusy(false)
    }
  }
  return (
    <Row
      icon={<Clock className="text-warning size-3.5" aria-hidden />}
      name={job.title}
      detail={`Stuck for ${age(job.createdAt)}`}
      to={job.deepLink}
      testId={`job-stuck-${job.id}`}
    >
      {job.actions.map((action) => (
        <Button
          key={action}
          variant={action === 'retry' ? 'outline' : 'ghost'}
          className="h-11 px-2.5"
          disabled={busy}
          onClick={() => void settle(action)}
          data-testid={`job-${action}-${job.id}`}
        >
          {ACTION_LABEL[action]}
        </Button>
      ))}
    </Row>
  )
}

export function JobsView() {
  const jobs = useJobsStore((s) => s.jobs)
  const pendingApprovals = usePendingApprovalsCount()
  const runs = useQuery({
    queryKey: [AGENT_RUN_ACTIVITY_KEY, 'running'],
    queryFn: () => listRuns({ status: 'running' }, 20),
    refetchInterval: POLL_MS,
  })
  const roles = useQuery({ queryKey: ['agent-roles'], queryFn: fetchAgentRoles })
  const roleName = (id: string | null) =>
    (id && roles.data?.find((r) => r.id === id)?.name) || 'Marketing'

  const agentRuns = runs.data ?? []
  const stuck = jobs.filter((j) => j.stuck)
  const active = byKind(jobs.filter((j) => ACTIVE.has(j.status) && !j.stuck))
  const [dismissed, dismissFailure] = useDismissedFailures()
  const jobKey = (job: JobItem) => `${job.source}:${job.id}`
  const failed = jobs.filter((j) => j.status === 'failed' && !dismissed.has(jobKey(j))).slice(0, 5)
  const recent = byKind(jobs.filter((j) => j.status === 'completed')).slice(0, RECENT_GROUPS)
  const spinner = <Loader2 className="text-primary size-3.5 animate-spin" aria-hidden />

  return (
    <div className="space-y-4" data-testid="jobs-view">
      {(pendingApprovals > 0 || stuck.length > 0) && (
        <Section title="Needs you">
          {pendingApprovals > 0 && (
            <Row
              icon={<ShieldAlert className="text-primary size-3.5" aria-hidden />}
              name={`${pendingApprovals} waiting for your OK`}
              detail="Approve, change or skip"
              to="/approvals"
              testId="jobs-needs-approval"
            />
          )}
          {stuck.map((job) => (
            <StuckRow key={`${job.source}-${job.id}`} job={job} />
          ))}
        </Section>
      )}
      <Section title="Running now">
        {agentRuns.length === 0 && active.length === 0 && (
          <li className="text-muted-foreground px-2 py-1.5 text-sm">Nothing is running.</li>
        )}
        {agentRuns.map((run) => (
          <Row
            key={run.id}
            icon={spinner}
            name={roleName(run.agent_role_id)}
            detail={`${latestStep(run)} · ${age(run.started_at)}`}
            to={run.agent_role_id ? `/chat/${run.agent_role_id}` : CHAT_THREAD_PATH}
          />
        ))}
        {active.map(([label, group]) => {
          const job = group[0]
          const progress = job.progress !== null && group.length === 1 ? `${job.progress}%` : null
          return (
            <Row
              key={label}
              icon={spinner}
              name={group.length > 1 ? `${label} · ${group.length}` : job.title}
              detail={[
                job.stage ?? (job.status === 'queued' ? 'Waiting to start' : 'Working'),
                progress,
                age(job.createdAt),
              ]
                .filter(Boolean)
                .join(' · ')}
              to={job.deepLink}
              testId={`job-item-${job.id}`}
            />
          )
        })}
      </Section>
      {failed.length > 0 && (
        <Section title="Failed">
          {failed.map((job) => (
            <Row
              key={`${job.source}-${job.id}`}
              icon={<AlertCircle className="text-destructive size-3.5" aria-hidden />}
              name={job.title}
              detail={describeJobFailure(job.errorMessage)?.message ?? 'Failed'}
              to={job.deepLink}
              testId={`job-item-${job.id}`}
            >
              <Button
                variant="ghost"
                className="h-11 px-2.5"
                onClick={() => dismissFailure(jobKey(job))}
                data-testid={`job-dismiss-failed-${job.id}`}
              >
                Dismiss
              </Button>
            </Row>
          ))}
        </Section>
      )}
      {recent.length > 0 && (
        <Section title="Done recently">
          {recent.map(([label, done]) => (
            <Row
              key={label}
              icon={<CheckCircle2 className="text-success size-3.5" aria-hidden />}
              name={done.length > 1 ? `${label} · ${done.length}` : done[0].title}
              detail={`Done ${age(done[0].completedAt ?? done[0].updatedAt)} ago`}
              to={done.length === 1 ? done[0].deepLink : null}
              testId={`job-item-${done[0].id}`}
            />
          ))}
        </Section>
      )}
    </div>
  )
}
